import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { isEmpty, isObject, pick, some } from 'lodash';
import {
  catchError,
  concat,
  concatMap,
  defer,
  forkJoin,
  from,
  map,
  merge,
  of,
  takeWhile
} from 'rxjs';
import { ContactForm } from 'src/app/classes/form-groups/contact-form';
import { ExemptVendorForm } from 'src/app/classes/form-groups/exempt-vendor-form';
import { LoadingMask } from 'src/app/classes/loading-mask';
import {
  Address,
  ExemptVendor,
  ExemptVendorType
} from 'src/app/classes/models';
import { SubNationalDivision } from 'src/app/classes/models/sub-national-division';
import { CompanyType } from 'src/app/interfaces/company-type';
import { LocalizationInfo } from 'src/app/interfaces/localization-info';
import { CompanyOffice } from 'src/app/interfaces/models/company-office';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { AccountingSystemSelectionService } from 'src/app/services/accounting-system-selection.service';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyService } from 'src/app/services/company.service';
import { ExemptVendorService } from 'src/app/services/exempt-vendor.service';
import { SubNationalDivisionService } from 'src/app/services/sub-national-division.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { apConfigsService } from 'src/app/shared/vive-api/ap-configs.service';
import { companiesService } from 'src/app/shared/vive-api/companies.service';
import { companyBlocklistedTaxIdsService } from 'src/app/shared/vive-api/company-blocklisted-tax-ids.service';
import {
  ApConfigDto,
  CompanyBlocklistedTaxIdDto,
  CompanyDto
} from 'src/app/shared/vive-api/vive-api-models';
import { LOAD_BALANCER_TIMEOUT_MS } from 'src/app/shared/vive-api/vive-api.config';
import { vendorNameLengthModalInputs } from 'src/app/validators/vendor-name-length-validator';

@Component({
  selector: 'app-add-edit-exempt-vendor',
  templateUrl: './add-edit-exempt-vendor.component.html',
  styleUrls: ['./add-edit-exempt-vendor.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AddEditExemptVendorComponent implements OnInit {
  constructor(
    private HostElement: ElementRef,
    private Router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private AuthService: AuthService,
    private ToastService: ToastService,
    private SubNationalDivisionService: SubNationalDivisionService,
    private CompanyService: CompanyService,
    private ExemptVendorService: ExemptVendorService,
    private UtilityService: UtilityService,
    private ModalService: NgbModal,
    private AccountingSystemSelectionService: AccountingSystemSelectionService,
    private cdr: ChangeDetectorRef
  ) {
    const user = this.AuthService.getUser();
    const { UserAccountType, UserAccountRole } = user;

    if (UserAccountType) {
      const { name } = UserAccountType;

      if (name === 'Client') {
        this.CompanyId = this.AuthService.getUserCompanyId();

        // Mark add-mode before getCompanyData() so the allowExemptVendors
        // gate runs even when the company response returns before ngOnInit.
        const ExemptVendorId = Number(
          this.route.snapshot.paramMap.get('ExemptVendorId')
        );
        if (!this.ExemptVendor && !ExemptVendorId) {
          this.addingExemptVendor = true;
        }

        this.getCompanyData();
        // Note: admin users (UserAccountType !== 'Client') intentionally bypass
        // the blocklist check — they can manage blocklist entries directly and
        // may need to add or edit vendors regardless of blocklist status.
      }
    }

    const { role } = UserAccountRole || {};
    if (
      role &&
      new Set([...this.fullSSNPermissions, ...this.maskedSSNPermissions]).has(
        role
      )
    ) {
      this.hasSSNAccess = true;
    }
  }

  @Input() isUploadedFromFile: boolean = false;
  @Input() ExemptVendor: ExemptVendor | null = null;
  @Input() CompanyId?: number | null;

  oldExemptVendorRecord: ExemptVendor | null = null;
  taxStructures: string[] | null = null;
  llcTypes: Map<string, string> | null = null;
  taxStructure: CompanyType | null = null;
  llcType: string | null = null;
  isMailingAndStreetSame: boolean = false;
  localizationInfo!: LocalizationInfo;
  states!: SubNationalDivision[];
  CompanyOffices: CompanyOffice[] = [];
  isTaxIdRequired: boolean = false;
  isEINRequired: boolean = false;
  isSSNRequired: boolean = false;
  // US Tax Id Masks
  einMask = this.UtilityService.einMask;
  ssnMask = this.UtilityService.ssnMask;
  phoneMask = this.UtilityService.phoneMask;

  selectedCompanyOfficeArray: CompanyOffice[] = [];
  ExemptVendorTypes: ExemptVendorType[] = [];
  ExemptVendorType: ExemptVendorType | null = null;
  addingExemptVendor: boolean = false;
  editingExemptVendor: boolean = false;

  ExemptVendorForm: ExemptVendorForm = new ExemptVendorForm();
  primaryContact: ContactForm = new ContactForm();
  additionalContacts: ContactForm[] = [];

  isSaving: boolean = false;

  // VPDW-763: accounting systems the user designated for this vendor's creation.
  selectedApConfigIds?: number[];
  blockedTaxIds: CompanyBlocklistedTaxIdDto[] = [];
  companyLegalName: string = '';
  maskedSSN?: string;
  fullSSN?: string;
  hasSSNAccess: boolean = false;

  fullSSNPermissions = [
    'ADMIN_ADMIN',
    'ADMIN_FRAUD_TEAM',
    'ADMIN_PROJECT_MANAGEMENT_TEAM'
  ];

  maskedSSNPermissions = [
    'ADMIN_CUSTOMER_SERVICE_TEAM',
    'ADMIN_INSURANCE_TEAM'
  ];

  setLLCTypes() {
    this.llcTypes = new Map([
      ['Partnership', 'P'],
      ['C-Corp', 'C'],
      ['S-Corp', 'S'],
      ['Disregarded Entity', 'disregardedEntity']
    ]);

    return;
  }

  showLLCTypes() {
    return ['Limited Liability Company (LLC)'].includes(this.taxStructure!);
  }

  setTaxStructures(country: 'US' | 'CA') {
    if (country === 'CA') {
      this.taxStructures = [
        'Sole Proprietorship',
        'Partnership',
        'Corporation'
      ];
    }

    if (country === 'US') {
      this.taxStructures = [
        'C Corporation',
        'S Corporation',
        'Limited Liability Company (LLC)',
        'Partnership',
        'Sole Proprietorship',
        'Trust'
      ];
    }
  }

  setTaxIdValidators() {
    const einControl = this.ExemptVendorForm.controls.ein;
    const ssnControl = this.ExemptVendorForm.controls.ssn;

    this.clearValidators(einControl, ssnControl);
    this.clearInvalidLlcType();

    const shouldShowSSN = this.shouldShowSSNField();
    this.handleFieldVisibilityAndMutualExclusion(
      einControl,
      ssnControl,
      shouldShowSSN
    );
    this.setRequiredValidators(einControl, ssnControl, shouldShowSSN);

    this.updateControls(einControl, ssnControl);
  }

  private clearValidators(einControl: any, ssnControl: any) {
    ssnControl?.clearValidators();
    einControl?.clearValidators();
    this.isEINRequired = false;
    this.isSSNRequired = false;
  }

  private clearInvalidLlcType() {
    const llcTypeControl = this.ExemptVendorForm.controls.llcType;
    const currentTaxStructure =
      this.ExemptVendorForm.controls.companyType?.value;

    if (
      currentTaxStructure !== 'Limited Liability Company (LLC)' &&
      llcTypeControl?.value
    ) {
      llcTypeControl.setValue(null, { emitEvent: false });
    }
  }

  private shouldShowSSNField(): boolean {
    const currentLlcType = this.ExemptVendorForm.controls.llcType?.value;
    const currentTaxStructure =
      this.ExemptVendorForm.controls.companyType?.value;

    return (
      currentTaxStructure === 'Sole Proprietorship' ||
      (currentTaxStructure === 'Limited Liability Company (LLC)' &&
        currentLlcType === 'disregardedEntity')
    );
  }

  private handleFieldVisibilityAndMutualExclusion(
    einControl: FormControl,
    ssnControl: FormControl,
    shouldShowSSN: boolean
  ) {
    const einValue = einControl?.value?.trim() || '';
    const ssnValue = ssnControl?.value?.trim() || '';

    const hasExistingSSN = this.editingExemptVendor && this.ExemptVendor?.ssn;

    if (!shouldShowSSN) {
      ssnControl?.patchValue('', { emitEvent: false });
      ssnControl?.disable({ emitEvent: false });

      this.maskedSSN = undefined;
      this.fullSSN = undefined;

      if (einControl?.disabled) {
        einControl.enable({ emitEvent: false });
      }
      return;
    }

    if (einValue && !ssnValue) {
      ssnControl?.patchValue('', { emitEvent: false });
      ssnControl?.disable({ emitEvent: false });
      if (einControl?.disabled) {
        einControl.enable({ emitEvent: false });
      }
    } else if (ssnValue && !einValue) {
      einControl?.patchValue('', { emitEvent: false });
      einControl?.disable({ emitEvent: false });
      if (ssnControl?.disabled) {
        ssnControl.enable({ emitEvent: false });
      }
    } else if (!einValue && !ssnValue) {
      if (
        hasExistingSSN &&
        this.hasSSNAccess &&
        !this.maskedSSN &&
        !this.fullSSN
      ) {
        if (einControl?.enabled) {
          einControl.disable({ emitEvent: false });
        }
        if (ssnControl?.disabled) {
          ssnControl.enable({ emitEvent: false });
        }
      } else {
        if (einControl?.disabled) {
          einControl.enable({ emitEvent: false });
        }
        if (ssnControl?.disabled) {
          ssnControl.enable({ emitEvent: false });
        }
      }
    }
  }

  private setRequiredValidators(
    einControl: FormControl,
    ssnControl: FormControl,
    shouldShowSSN: boolean
  ) {
    if (!this.isTaxIdRequired || this.isUploadedFromFile) {
      return;
    }

    const einValue = einControl?.value?.trim() || '';
    const ssnValue = ssnControl?.value?.trim() || '';

    if (shouldShowSSN) {
      if (einValue) {
        einControl?.setValidators([Validators.required]);
        this.isEINRequired = true;
      } else if (ssnValue) {
        ssnControl?.setValidators([Validators.required]);
        this.isSSNRequired = true;
      }
    } else if (!einValue) {
      einControl?.setValidators([Validators.required]);
      this.isEINRequired = true;
    }
  }

  private updateControls(einControl: FormControl, ssnControl: FormControl) {
    einControl?.updateValueAndValidity({ emitEvent: false });
    ssnControl?.updateValueAndValidity({ emitEvent: false });
    this.cdr.detectChanges();
  }

  setExemptVendorType() {
    const exemptVendorType = this.ExemptVendorTypes.find(
      (type) =>
        type.id === this.ExemptVendorForm.controls.ExemptVendorTypeId?.value
    );

    if (exemptVendorType) {
      this.ExemptVendorType = exemptVendorType;
      const { isTaxIdRequired } = this.ExemptVendorType;
      this.isTaxIdRequired = isTaxIdRequired!;
    }

    this.setTaxIdValidators();
  }

  onTaxStructureChange(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    this.taxStructure = selectElement.value as CompanyType;
    this.setLLCTypes();
    this.setTaxIdValidators();
  }

  onLLCTypeChange(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    if (selectElement) {
      this.llcType = this.llcTypes!.get(selectElement.value)!;
      this.ExemptVendorForm.controls.llcType?.setValue(
        selectElement.value as 'C' | 'S' | 'P' | 'disregardedEntity',
        { emitEvent: false }
      );
    }
    this.setTaxIdValidators();
  }

  onIsMailingAndStreetSameChange() {
    this.isMailingAndStreetSame = !this.isMailingAndStreetSame;

    const MailingAddress = this.ExemptVendorForm.controls.MailingAddress;
    const StreetAddress = this.ExemptVendorForm.controls.StreetAddress;

    if (this.isMailingAndStreetSame && StreetAddress) {
      StreetAddress.disable();
      StreetAddress.patchValue(
        pick(MailingAddress.value, [
          'address1',
          'address2',
          'city',
          'state',
          'zip'
        ])
      );
    } else if (!this.isMailingAndStreetSame && StreetAddress) {
      StreetAddress.enable();

      StreetAddress.patchValue({
        address1: undefined,
        address2: undefined,
        city: undefined,
        state: undefined,
        zip: undefined
      });
    }
  }

  addContact() {
    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot add new contact. No Company ID set.',
        { returnError: true }
      );
    }

    const newContactForm = new ContactForm();
    newContactForm.controls.StreetAddress?.controls.isStreet.setValue(true);

    this.additionalContacts.push(newContactForm);
  }

  goToVendorsScreen() {
    this.Router.navigateByUrl(
      `client/${this.AuthService.getUserCompanyId()}/vendors`
    );
  }

  setExemptVendor() {
    if (!this.ExemptVendor) {
      this.ExemptVendor = {};
    }

    const contactFormsArr: ContactForm[] = [];
    const primaryContact = this.primaryContact;
    const additionalContacts = this.additionalContacts;

    const mailingAddressControl = this.ExemptVendorForm.controls.MailingAddress;
    const streetAddressControl = this.ExemptVendorForm.controls.StreetAddress;

    streetAddressControl.patchValue({ isStreet: true });
    mailingAddressControl.patchValue({ isMailing: true });

    streetAddressControl.updateValueAndValidity();
    mailingAddressControl.updateValueAndValidity();

    primaryContact.patchValue({
      isPrimaryCompliance: true,
      isCompliance: true
    });

    primaryContact.updateValueAndValidity();

    const hasValues = (obj: {}): boolean => {
      if (!obj || typeof obj !== 'object') return false;

      const hasValidValue = (value: any) => value !== null && value !== false;

      return Object.values(obj).some((value) => {
        if (isObject(value)) {
          return hasValues(value); // Recursively check for nested objects
        }
        return hasValidValue(value);
      });
    };

    Object.assign(this.ExemptVendor, this.ExemptVendorForm.value);

    if (
      mailingAddressControl.value &&
      !hasValues(
        pick(mailingAddressControl.value, [
          'address1',
          'address2',
          'city',
          'state',
          'zip'
        ])
      )
    ) {
      delete this.ExemptVendor.MailingAddress;
    }

    if (
      streetAddressControl.value &&
      !hasValues(
        pick(streetAddressControl.value, [
          'address1',
          'address2',
          'city',
          'state',
          'zip'
        ])
      )
    ) {
      delete this.ExemptVendor.StreetAddress;
    } else {
      // Doing this because if street address gets set to same as mailing address
      // it is disabled in the form and doesn't get assigned to the ExemptVendor object.
      this.ExemptVendor.StreetAddress = streetAddressControl.value;
    }
    // Ensure only one tax ID is sent to backend - clear the inactive one
    const shouldShowSSN = this.shouldShowSSNField();
    const einValue = this.ExemptVendorForm.controls.ein?.value?.trim() || '';
    const ssnValue = this.ExemptVendorForm.controls.ssn?.value?.trim() || '';

    if (!shouldShowSSN) {
      // SSN field is not visible, clear it from the payload
      (this.ExemptVendor as any).ssn = null;
    } else if (shouldShowSSN) {
      // Both fields are potentially visible, but only one should have a value
      if (einValue && !ssnValue) {
        (this.ExemptVendor as any).ssn = null;
      } else if (ssnValue && !einValue) {
        (this.ExemptVendor as any).ein = null;
      }
    }

    // Need CompanyId for some backend checks
    (this.ExemptVendor as any).CompanyId = this.CompanyId;

    if (
      primaryContact &&
      hasValues(
        pick(primaryContact.value, ['firstName', 'lastName', 'phone', 'email'])
      )
    ) {
      primaryContact.controls.ExemptVendorId.setValue(
        this.ExemptVendor.id || null
      );
      primaryContact.controls.CompanyId;

      if (
        primaryContact.controls.StreetAddress &&
        hasValues(primaryContact.controls.StreetAddress.value)
      ) {
        primaryContact.controls.StreetAddress.controls.isStreet.setValue(true);
      }

      contactFormsArr.push(primaryContact);
    }

    if (additionalContacts && additionalContacts.length > 0) {
      additionalContacts.forEach((contact: ContactForm) => {
        if (hasValues(contact.value)) {
          contact.controls.ExemptVendorId.setValue(
            this.ExemptVendor?.id || null
          );
          contactFormsArr.push(contact);
        }
      });
    }

    if (contactFormsArr.length > 0) {
      contactFormsArr.forEach((form) => {
        const mailingAddress = form.controls.MailingAddress;
        const streetAddress = form.controls.StreetAddress;

        if (
          mailingAddress &&
          !hasValues(
            pick(mailingAddress.value, [
              'address1',
              'address2',
              'city',
              'state',
              'zip'
            ])
          )
        ) {
          form.removeControl('MailingAddress');
        }

        if (
          streetAddress &&
          !hasValues(
            pick(streetAddress.value, [
              'address1',
              'address2',
              'city',
              'state',
              'zip'
            ])
          )
        ) {
          form.removeControl('StreetAddress');
        }

        if (hasValues(form.value)) {
          this.ExemptVendor?.Contacts?.push(form.value);
        }
      });
    }
  }

  viewSSN() {
    if (!this.ExemptVendor?.id) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot view SSN. Missing Exempt Vendor ID.',
        { returnError: true }
      );
    }

    const user = this.AuthService.getUser();
    if (!user) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing user information.',
        { returnError: true }
      );
    }

    const { UserAccountRole: { role } = {} } = user;

    if (!role) {
      throw this.ToastService.addErrorToast('Error', 'Missing user role.', {
        returnError: true
      });
    }

    if (new Set(this.fullSSNPermissions).has(role)) {
      return this.ExemptVendorService.getExemptVendor(
        this.UtilityService.stringifyObjectsInQueryObject({
          id: this.ExemptVendor.id,
          attributes: ['id', 'ssn']
        })
      ).subscribe({
        next: (ExemptVendor) => {
          if (!ExemptVendor) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Cannot view SSN. Missing Exempt Vendor information.',
              { returnError: true }
            );
          }

          const { ssn } = ExemptVendor;

          if (!ssn) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Cannot view SSN. Missing SSN information.',
              { returnError: true }
            );
          }

          Object.assign(this, { fullSSN: ssn });
          this.ExemptVendorForm.controls.ssn.setValue(ssn, {
            emitEvent: false
          });
          this.cdr.detectChanges();
        }
      });
    }

    if (new Set(this.maskedSSNPermissions).has(role)) {
      return this.ExemptVendorService.getExemptVendor(
        this.UtilityService.stringifyObjectsInQueryObject({
          id: this.ExemptVendor.id,
          attributes: ['id'],
          aliasedAttributes: ['maskedSSN']
        })
      ).subscribe({
        next: (ExemptVendor: any) => {
          if (!ExemptVendor) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Cannot view SSN. Missing Exempt Vendor information.',
              { returnError: true }
            );
          }

          const { maskedSSN } = ExemptVendor;

          if (!maskedSSN) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Cannot view SSN. Missing masked SSN information.',
              { returnError: true }
            );
          }

          Object.assign(this, { maskedSSN });
          this.cdr.detectChanges();
        }
      });
    }

    return;
  }

  cancel() {
    this.location.back();
  }

  private isBlockedTaxId(): boolean {
    const stripNonDigits = (value: string) => value.replace(/\D/g, '');

    const einValue = stripNonDigits(
      this.ExemptVendorForm.controls.ein?.value?.trim() || ''
    );
    const ssnValue = stripNonDigits(
      this.ExemptVendorForm.controls.ssn?.value?.trim() || ''
    );

    return this.blockedTaxIds.some(
      (blocked) =>
        (einValue &&
          blocked.taxIdType === 'EIN' &&
          blocked.taxId === einValue) ||
        (ssnValue && blocked.taxIdType === 'SSN' && blocked.taxId === ssnValue)
    );
  }

  save() {
    try {
      this.isSaving = true;

      const validationErrorsObj =
        this.UtilityService.generateValidationErrorsObject(
          this.ExemptVendorForm
        );

      if (validationErrorsObj) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Cannot Add Vendor. You have missing required information.',
          { returnError: true }
        );
      }

      const { legalName, dba } = this.ExemptVendorForm.value;
      const nameLengthModalInputs = vendorNameLengthModalInputs({
        legalName,
        dba
      });
      if (nameLengthModalInputs) {
        const { componentInstance } = this.ModalService.open(
          ConfirmModalComponent,
          { centered: true }
        );
        Object.assign(componentInstance, nameLengthModalInputs);
        this.isSaving = false;
        return;
      }

      if (this.isBlockedTaxId()) {
        throw this.ToastService.addErrorToast(
          'Error',
          `This vendor's Tax ID has been blocked by ${this.companyLegalName || 'this client'} and cannot be added or updated on the platform. If you need to make changes, please contact VIVE's client support at clientsupport@getvived.com to discuss removal from their block list.`,
          { returnError: true }
        );
      }

      this.setExemptVendor();

      if (!this.ExemptVendor) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Cannot Add Vendor. No ExemptVendor details found.',
          { returnError: true }
        );
      }

      if (!this.CompanyId) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Cannot Add Vendor. No Company ID set.',
          { returnError: true }
        );
      }

      forkJoin([
        from(
          apConfigsService.findAllForClient({
            pathParams: { companyId: this.CompanyId }
          })
        ).pipe(map(({ data }) => data)),
        // Notifies if the Tax ID is already registered for that client
        this.ExemptVendorService.checkForDuplicateVendorTaxId(this.ExemptVendor)
      ]).subscribe({
        next: ([companyApConfigs, registeredVendorsWithDuplicateTaxIds]: [
          ApConfigDto[],
          ExemptVendor[]
        ]) => {
          const observableArr = [];

          if (!isEmpty(registeredVendorsWithDuplicateTaxIds)) {
            observableArr.push(
              defer(() => {
                const duplicateExemptVendorModal = this.ModalService.open(
                  ConfirmModalComponent,
                  { centered: true }
                );
                const vendorNames = registeredVendorsWithDuplicateTaxIds
                  .map((v: ExemptVendor) => v.legalName)
                  .filter(Boolean)
                  .join(', ');
                duplicateExemptVendorModal.componentInstance.displayText = `A Vendor with this Tax ID already exists in the system${vendorNames ? ` (${vendorNames})` : ''}. You may still proceed.`;

                return merge(
                  duplicateExemptVendorModal.closed,
                  duplicateExemptVendorModal.dismissed.pipe(map(() => false))
                );
              })
            );
          }

          observableArr.push(
            defer(() => {
              const confirmSaveExemptVendorModal = this.ModalService.open(
                ConfirmModalComponent,
                { centered: true }
              );
              confirmSaveExemptVendorModal.componentInstance.displayText = `Are you sure you want to ${this.editingExemptVendor ? 'modify' : 'add'} this Compliance Exempt Vendor?`;

              return merge(
                confirmSaveExemptVendorModal.closed,
                confirmSaveExemptVendorModal.dismissed.pipe(map(() => false))
              );
            })
          );

          // VPDW-763: on NEW exempt-vendor creation, let the user designate which
          // accounting system(s) it is created in (only prompts with 2+ eligible
          // systems). Skipped when editing — no AP vendor is created on edit.
          if (!this.editingExemptVendor) {
            observableArr.push(
              defer(() =>
                this.AccountingSystemSelectionService.selectApConfigIds(
                  this.CompanyId ?? undefined
                ).pipe(
                  concatMap((apConfigIds) => {
                    if (apConfigIds === null) {
                      return of(false);
                    }

                    this.selectedApConfigIds = apConfigIds ?? undefined;

                    return of(true);
                  })
                )
              )
            );
          }

          observableArr.push(
            defer(() => {
              // Only used if AP config has allowCreateVendors to warn possible duplicated in client's accounting system
              return forkJoin([
                this.ExemptVendorService.checkForDuplicateTaxId(
                  this.ExemptVendor!,
                  { apConfigIds: this.selectedApConfigIds }
                ),
                this.ExemptVendorService.checkForDuplicateLegalName(
                  this.ExemptVendor!,
                  { apConfigIds: this.selectedApConfigIds }
                )
              ]).pipe(
                concatMap(
                  ([
                    vendorsWithDuplicateTaxIds,
                    vendorsWithDuplicateLegalName
                  ]) => {
                    if (
                      (!isEmpty(vendorsWithDuplicateTaxIds) ||
                        !isEmpty(vendorsWithDuplicateLegalName)) &&
                      some(
                        companyApConfigs,
                        (config) =>
                          config.apSystem &&
                          (config.apSystem === 'Cinc' ||
                            config.apSystem === 'Vantaca' ||
                            config.apSystem === 'Yardi' ||
                            config.apSystem === 'MRI' ||
                            config.apSystem === 'RentManager' ||
                            config.apSystem === 'Caliber')
                      )
                    ) {
                      const duplicateAlertModal = this.ModalService.open(
                        ConfirmModalComponent,
                        { centered: true }
                      );
                      duplicateAlertModal.componentInstance.displayText =
                        'There already exists a vendor with this legal name and/or Tax ID. If this Vendor is in your accounting system already, VIVE will not create a new vendor in your AP System. Please manually link this vendor once it has been saved.';

                      return merge(
                        duplicateAlertModal.closed,
                        duplicateAlertModal.dismissed.pipe(map(() => false))
                      );
                    }

                    return of(true);
                  }
                )
              );
            })
          );

          observableArr.push(
            defer(() => {
              const { nativeElement } = this.HostElement;
              const mask = new LoadingMask(nativeElement, {
                maskText: 'Saving Exempt Vendor...'
              });

              return this.ExemptVendorService.saveExemptVendor(
                this.ExemptVendor!,
                {
                  sendCreationEmail: true,
                  ...(this.selectedApConfigIds &&
                  this.selectedApConfigIds.length
                    ? { apConfigIds: this.selectedApConfigIds }
                    : {})
                }
              ).pipe(
                concatMap(() => {
                  mask.destroy();
                  this.location.back();
                  this.ToastService.addSuccessToast(
                    'Success',
                    'Successfully saved Compliance Exempt Vendor'
                  );
                  return of(true);
                }),
                catchError(() => {
                  mask.destroy();
                  return of(false);
                })
              );
            })
          );

          concat(...observableArr)
            .pipe(takeWhile((result) => result !== false))
            .subscribe({
              next: () => {
                this.isSaving = false;
              },
              error: () => {
                this.isSaving = false;
                this.cdr.markForCheck();
              },
              complete: () => {
                this.isSaving = false;
                this.cdr.markForCheck();
              }
            });
        },
        error: (err) => {
          this.isSaving = false;
          this.cdr.markForCheck();
          throw this.ToastService.addErrorToast('Error', err.message, {
            returnError: true
          });
        }
      });
    } catch (error) {
      this.isSaving = false;
      this.cdr.markForCheck();
    }
  }

  loadExemptVendor() {
    this.editingExemptVendor = true;
    this.oldExemptVendorRecord = structuredClone(this.ExemptVendor);

    if (!this.oldExemptVendorRecord) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error trying to copy this compliance exempt vendor record',
        { returnError: true }
      );
    }

    this.ExemptVendorForm.patchValue(this.oldExemptVendorRecord);

    const { Contacts, StreetAddress, MailingAddress, CompanyVendors } =
      this.oldExemptVendorRecord;

    if (CompanyVendors && CompanyVendors.length > 1) {
      throw this.ToastService.addErrorToast(
        'Error',
        'This exempt vendor has multiple clients.',
        { returnError: true }
      );
    }

    if (CompanyVendors && CompanyVendors.length === 1) {
      const companyVendor = CompanyVendors[0];
      const { CompanyId } = companyVendor;

      this.CompanyId = CompanyId;

      if (this.CompanyId) {
        this.getCompanyData();
      }
    }

    if (Contacts && Contacts.length > 0) {
      const primaryContact = Contacts.find(
        (contact) => contact.isPrimaryCompliance
      );
      const additionalContacts = Contacts.filter(
        (contact) => !contact.isPrimaryCompliance
      );

      if (primaryContact) {
        this.primaryContact?.patchValue(primaryContact);
      }

      if (additionalContacts && additionalContacts.length > 0) {
        additionalContacts.forEach((contact) => {
          const additionalContactForm = new ContactForm();
          additionalContactForm.patchValue(contact);

          this.additionalContacts.push(additionalContactForm);
        });
      }
    }

    if (StreetAddress) {
      this.ExemptVendorForm.controls.StreetAddress.patchValue(StreetAddress);
    }

    if (MailingAddress) {
      this.ExemptVendorForm.controls.MailingAddress.patchValue(MailingAddress);
    }

    const fieldsToCheck: (keyof Address)[] = [
      'address1',
      'address2',
      'city',
      'state',
      'zip'
    ];

    if (StreetAddress && MailingAddress) {
      const isStreetAndMailingSame = fieldsToCheck.every(
        (field: keyof Address) => {
          if (field !== null) {
            return StreetAddress[field] === MailingAddress[field];
          }

          return false;
        }
      );

      if (isStreetAndMailingSame) {
        this.onIsMailingAndStreetSameChange();
      }
    }
  }

  getCompanyData() {
    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot get company info. No Company ID.',
        { returnError: true }
      );
    }

    forkJoin([
      this.CompanyService.getCompanyExemptVendorTypes(this.CompanyId),
      from(
        companiesService.findOneById({
          pathParams: { companyId: this.CompanyId }
        })
      ),
      // VPDW-4054: this runs as CLIENT_ADMIN, which is not allowed the `Note`
      // association. isBlockedTaxId() only compares taxId/taxIdType, so do not
      // include the Note association here — it would 400 the whole screen on load.
      from(
        companyBlocklistedTaxIdsService.findAllByCompanyId({
          pathParams: { companyId: this.CompanyId },
          options: { requestTimeout: LOAD_BALANCER_TIMEOUT_MS }
        })
      )
    ]).subscribe({
      next: ([ExemptVendorTypes, Company, blockedTaxIds]: [
        ExemptVendorType[],
        CompanyDto,
        CompanyBlocklistedTaxIdDto[]
      ]) => {
        this.blockedTaxIds = blockedTaxIds || [];

        if (!Company) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Cannot Add Vendor. No Company Record Found',
            { returnError: true }
          );
        }

        if (this.addingExemptVendor && !Company.allowExemptVendors) {
          this.ToastService.addErrorToast(
            'Error',
            'This client is not configured to add Compliance Exempt vendors.',
            { returnError: false }
          );
          this.Router.navigate(['client', this.CompanyId, 'vendors', 'client']);
          return;
        }

        if (!ExemptVendorTypes || ExemptVendorTypes.length === 0) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Cannot Add Vendor. No Company Exempt Vendor Types.',
            { returnError: true }
          );
        }

        this.ExemptVendorTypes = ExemptVendorTypes;

        this.companyLegalName = Company.legalName || '';

        const { country } = Company;

        if (!country) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Client has no country.',
            { returnError: true }
          );
        }

        this.localizationInfo =
          this.UtilityService.getLocalizedValuesMap(country);
        this.SubNationalDivisionService.getSubNationalDivisions({
          country: country === 'US' ? 'USA' : 'Canada'
        }).subscribe({
          next: (data: SubNationalDivision[]) => {
            this.states = data;
            this.cdr.markForCheck();
          }
        });

        this.setTaxStructures(country);
        this.cdr.markForCheck();
      }
    });
  }

  ngOnInit(): void {
    const paramMap = this.route.snapshot.paramMap;
    const ExemptVendorId = Number(paramMap.get('ExemptVendorId'));

    if (this.ExemptVendor) {
      this.loadExemptVendor();
    } else if (ExemptVendorId) {
      this.ExemptVendorService.getExemptVendor(
        this.UtilityService.stringifyObjectsInQueryObject({
          id: ExemptVendorId,
          isArchived: { $in: [true, false] },
          StreetAddress: {},
          MailingAddress: {},
          Contact: {},
          CompanyVendor: {}
        })
      ).subscribe({
        next: (ExemptVendor: ExemptVendor) => {
          this.ExemptVendor = ExemptVendor;

          this.loadExemptVendor();
          this.cdr.markForCheck();
        }
      });
    } else {
      this.addingExemptVendor = true;
    }

    this.setTaxIdValidators();

    const MailingAddressControl = this.ExemptVendorForm.controls.MailingAddress;
    const StreetAddressControl = this.ExemptVendorForm.controls.StreetAddress;

    MailingAddressControl.valueChanges.subscribe({
      next: (changes) => {
        if (changes && this.isMailingAndStreetSame) {
          StreetAddressControl.patchValue(
            pick(changes, ['address1', 'address2', 'city', 'state', 'zip'])
          );

          StreetAddressControl.updateValueAndValidity();
          this.cdr.markForCheck();
        }
      }
    });

    this.ExemptVendorForm.controls.companyType.valueChanges.subscribe({
      next: (value) => {
        if (value) {
          this.taxStructure = value as CompanyType;
          this.setLLCTypes();
          this.setTaxIdValidators();
        }
      }
    });

    this.ExemptVendorForm.controls.ein.valueChanges.subscribe({
      next: (value) => {
        this.setTaxIdValidators();
      }
    });

    this.ExemptVendorForm.controls.ssn.valueChanges.subscribe({
      next: (value) => {
        this.setTaxIdValidators();
      }
    });

    if (this.ExemptVendorForm.controls.llcType) {
      this.ExemptVendorForm.controls.llcType.valueChanges.subscribe({
        next: (value) => {
          this.setTaxIdValidators();
        }
      });
    }
  }
}
